import isNil from 'lodash/isNil'
import { useCallback, useMemo } from 'react'
import { useInfiniteQuery } from 'react-query'

import { useBankAccountByBookkeepingAccountId } from '@features/bankAccounts/hooks/useBankAccountByBookkeepingAccountId'
import { BankLineStatus } from '@features/reconciliation/enums/bankLineStatus'
import { BankLine } from '@modules-deprecated/bankReconciliation/types'

import { defaultStaleTime } from '../../../config/queryClient'
import { PeriodMode } from '../../../enums/periodMode'
import { QueryKeys } from '../../../enums/queryKeys'
import { SortDirection } from '../../../enums/sortDirection'
import { BankTransactionSortProperty } from '../enums/bankTransactionSortProperty'
import { TransactionTypeFilter } from '../enums/transactionTypeFilter'
import { BankLinesQueryParams, BankLinesResponse, fetchBankLines } from '../query-api'

export interface UseFetchBankTransactionsProps {
  accountId: string
  enabled?: boolean
  isReconciled?: boolean
  transactionTypeFilter?: TransactionTypeFilter
  maxAmount?: number
  minAmount?: number
  period?: string
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: BankTransactionSortProperty
  status?: BankLineStatus
}

export interface UseFetchBankTransactionsResponse {
  bankTransactions: BankLine[]
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
}

export const useFetchBankTransactions = ({
  accountId,
  enabled = true,
  isReconciled,
  transactionTypeFilter,
  maxAmount,
  minAmount,
  period,
  searchQuery,
  sortDirection,
  sortProperty,
  status,
}: UseFetchBankTransactionsProps): UseFetchBankTransactionsResponse => {
  const { reconciliationStartDate } = useBankAccountByBookkeepingAccountId(accountId, { enabled })

  // Falls back to the account's reconciliation start date so transactions from before it
  // don't show up as reconcilable, mirroring the legacy bank-sync-matches behavior.
  const entryDatePeriod = useMemo(() => {
    if (period && period !== PeriodMode.All) {
      return period
    }

    if (isReconciled !== true && reconciliationStartDate) {
      return `${PeriodMode.CustomDateFrom}:${reconciliationStartDate}`
    }

    return period
  }, [period, isReconciled, reconciliationStartDate])

  const queryProps: BankLinesQueryParams = useMemo(() => {
    return {
      accountId,
      ...(!isNil(isReconciled) ? { isReconciled } : {}),
      ...(!isNil(maxAmount) ? { maxAmount } : {}),
      ...(!isNil(minAmount) ? { minAmount } : {}),
      ...(entryDatePeriod ? { entryDatePeriod } : {}),
      ...(searchQuery ? { q: searchQuery } : {}),
      ...(transactionTypeFilter ? { side: transactionTypeFilter } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      ...(sortProperty ? { sortProperty } : {}),
      ...(status ? { status } : {}),
    }
  }, [
    accountId,
    isReconciled,
    transactionTypeFilter,
    maxAmount,
    minAmount,
    entryDatePeriod,
    searchQuery,
    sortDirection,
    sortProperty,
    status,
  ])

  const queryKey = useMemo(() => [QueryKeys.BankLines, queryProps] as const, [queryProps])

  const handleGetNextPage = useCallback((lastPage: BankLinesResponse) => {
    const paging = lastPage.meta?.paging

    if (!paging || paging.page >= paging.pageCount) {
      return undefined
    }

    return paging.page + 1
  }, [])

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) => fetchBankLines({ ...queryProps, page: pageParam, pageSize: 200 }),
    {
      enabled: !!accountId && enabled,
      staleTime: defaultStaleTime,
      getNextPageParam: handleGetNextPage,
    },
  )

  const bankTransactions = useMemo(() => {
    const allTransactions = data?.pages.flatMap((page) => page.bankLines ?? []) ?? []
    const transactionsById = new Map(
      allTransactions.filter(Boolean).map((transaction) => [transaction.id, transaction]),
    )

    return [...transactionsById.values()]
  }, [data?.pages])

  return {
    bankTransactions,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isLoading,
  }
}
